import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getCurrentUser } from '@/lib/supabase';
import { 
  getConversations, 
  getMessages, 
  sendMessage, 
  markMessageAsRead,
  markMessagesAsRead,
  subscribeToConversation,
  Conversation,
  Message
} from '@/services/messageService';
import { toast } from 'sonner';

export const useParentMessages = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    const getUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };
    getUser();
  }, []);

  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        const data = await getConversations();
        if (!currentUser) {
          setConversations(data);
          setLoading(false);
          return;
        }
        // PERFORMANCE: Optimize N+1 queries by batching organization fetches
        
        // First, get all unique organization IDs
        const orgIds = [...new Set(data.map(conv => conv.organization_id).filter(Boolean))];
        
        // Batch fetch all organizations at once
        const { data: organizations = [], error: orgError } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', orgIds);
          
        if (orgError) {
          // Error fetching organizations
        }
        
        // Create organization lookup map
        const orgMap = organizations.reduce((map, org) => {
          map[org.id] = org.name;
          return map;
        }, {} as Record<string, string>);

        // Enhance each conversation with lastMessage, unreadCount, and organization info
        const enhanced = await Promise.all(
          data.map(async (conv) => {
            // Fetch the most recent message
            const messages = await getMessages(conv.id, currentUser.id);
            const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
            // Compute unread count (messages not sent by current user and not marked as read)
            const unreadCount = messages.filter(m => m.sender_id !== currentUser.id && !m.is_read).length;
            
            // Get organization name from lookup map (no additional query needed)
            const organizationName = orgMap[conv.organization_id] || 'Unknown Organization';
            
            return {
              ...conv,
              lastMessage,
              unreadCount,
              organization: { name: organizationName }
            };
          })
        );
        setConversations(enhanced);
        setLoading(false);
      } catch (error) {
        // Error fetching conversations
        toast.error('Could not load conversations');
        setLoading(false);
      }
    };
    loadConversations();
  }, [currentUser]);

  useEffect(() => {
    if (!conversationId) return;

    const loadConversation = async () => {
      try {
        const conversation = await getConversations().then(convs => 
          convs.find(c => c.id === conversationId)
        );
        setCurrentConversation(conversation || null);
      } catch (error) {
        // Error fetching conversation
      }
    };

    const loadMessages = async () => {
      try {
        if (!currentUser) return;
        
        const data = await getMessages(conversationId, currentUser.id);
        setMessages(data);

        if (currentUser && data.length > 0) {
          // Find all unread messages not sent by the parent
          const unreadMessages = data.filter(m => 
            m.sender_id !== currentUser.id &&
            !m.is_read
          );
          if (unreadMessages.length > 0) {
            await markMessagesAsRead(unreadMessages.map(m => m.id), currentUser.id);
            // Wait a short moment to ensure DB is updated before refreshing
            setTimeout(refreshConversations, 150);
          }
        }
      } catch (error) {
        // Error fetching messages
        toast.error('Could not load messages');
      }
    };

    // After marking as read, refresh conversations to update unread counts
    const refreshConversations = async () => {
      if (!currentUser) return;
      const data = await getConversations();
      
      // PERFORMANCE: Optimize N+1 queries by batching organization fetches
      const orgIds = [...new Set(data.map(conv => conv.organization_id).filter(Boolean))];
      
      // Batch fetch all organizations at once
      const { data: organizations = [], error: orgError } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', orgIds);
        
      if (orgError) {
        console.error('Error fetching organizations:', orgError);
      }
      
      // Create organization lookup map
      const orgMap = organizations.reduce((map, org) => {
        map[org.id] = org.name;
        return map;
      }, {} as Record<string, string>);
      
      // Enhance each conversation with lastMessage, unreadCount, and organization info
      const enhanced = await Promise.all(
        data.map(async (conv) => {
          const messages = await getMessages(conv.id, currentUser.id);
          const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
          const unreadCount = messages.filter(m => m.sender_id !== currentUser.id && !m.is_read).length;
          
          // Get organization name from lookup map (no additional query needed)
          const organizationName = orgMap[conv.organization_id] || 'Unknown Organization';
          
          return {
            ...conv,
            lastMessage,
            unreadCount,
            organization: { name: organizationName }
          };
        })
      );
      setConversations(enhanced);
    };

    loadConversation();
    loadMessages().then(refreshConversations);

    const subscription = subscribeToConversation(conversationId, async (newMessage) => {
      setMessages(prev => [...prev, newMessage] as Message[]);
      
      if (currentUser && newMessage.sender_id !== currentUser.id) {
        await markMessageAsRead(newMessage.id, currentUser.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId, currentUser]);

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || !conversationId || !currentUser) return;

    try {
      // Get the parent row for the current user
      const { data: parentRow, error: parentError } = await supabase
        .from('parents')
        .select('id, organization_id')
        .eq('user_id', currentUser.id)
        .single();

      if (parentError || !parentRow) {
        // Error getting parent row
        toast.error('Failed to send message');
        return;
      }

      // Use the parent's row id and organization_id for the message
      await sendMessage(
        conversationId, 
        parentRow.id, // sender_id must be parent's row id
        'parent', 
        messageText.trim(),
        parentRow.organization_id
      );
      setNewMessage('');
    } catch (error) {
      // Error sending message
      toast.error('Failed to send message');
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    return conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (conv.family && conv.family.name.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  return {
    conversationId,
    conversations: filteredConversations,
    messages,
    newMessage,
    setNewMessage,
    currentConversation,
    searchTerm,
    setSearchTerm,
    loading,
    currentUser,
    handleSendMessage,
    navigate
  };
};
